// ------------------------------------------------------------------
// StateMatrix — story 2-147p (P10 §2.6/§4.2, DOD). Renders the SAME named
// state set every layout family in P10 §2.6 already requires and P10
// §4.2's "admitting a seventh layout family" bar names explicitly:
// long-content, loading, error, locked, responsive. A grid of labeled
// cells, one per state, each rendering whatever content the caller
// supplies for that state — generic over WHICH primitive is being shown,
// fixed over WHICH states a layout-family-grade demonstration must cover.
//
// `LAYOUT_FAMILY_STATE_SET` names the canonical five, in P10 §2.6/§4.2's
// own order, so a consumer building a real state-matrix demo (this story's
// own worked proof; any future layout-family admission per §4.2) maps over
// one shared list instead of re-typing the five keys/labels per call site.
// A consumer MAY pass a narrower or different `states` array (StateMatrix
// itself does not enforce the five — see the component's own doc comment
// below) but the five above are what P10 §2.6/§4.2 name as the CANONICAL
// set this story's own done-when requires demonstrating at least once.
// ------------------------------------------------------------------

import { Surface } from '../ui/Surface';
import { Grid } from '../ui/Layout';

export interface StateMatrixEntry {
  key: string;
  label: string;
  /**
   * Spans the full grid width instead of sharing a single column — for
   * content that genuinely needs more room than one cell can give it (the
   * canonical case: the "responsive" state's own `ResponsivePreview`, whose
   * multiple fixed-width breakpoint frames need far more than a ~330px
   * column). Other states stay single-column and `overflow-hidden`-bounded
   * on purpose — see the cell's own comment below.
   */
  fullWidth?: boolean;
  children: React.ReactNode;
}

export interface StateMatrixProps {
  states: StateMatrixEntry[];
}

// P10 §2.6 / §4.2's canonical five-state set, named once. Order matches
// the doc's own prose ("long-content, loading, error, locked, responsive").
export const LAYOUT_FAMILY_STATE_SET: readonly { key: string; label: string }[] = [
  { key: 'longContent', label: 'Long content' },
  { key: 'loading', label: 'Loading' },
  { key: 'error', label: 'Error' },
  { key: 'locked', label: 'Locked' },
  { key: 'responsive', label: 'Responsive' },
];

export function StateMatrix({ states }: StateMatrixProps): JSX.Element {
  return (
    <Grid as="dl" columns={{ base: 1, sm: 2, lg: 3 }} gap={4} data-testid="state-matrix">
      {states.map((s) => (
        // Story 2-147ta: composes `Surface` (flat, `md` padding — the same
        // p-4 every cell always rendered) instead of re-deriving the
        // border/bg/radius by hand.
        <Surface
          key={s.key}
          as="div"
          variant="flat"
          padding="md"
          // `min-w-0` — this cell is a CSS grid item; without it a
          // shrink-0/nowrap child (the long-content state's own worked
          // proof) forces the grid track wider than its column instead of
          // being bounded by it, the identical "text-bearing grid child
          // needs min-w-0" rule the content-stress matrix Section above
          // already establishes tree-wide. A single-column cell also gets
          // `overflow-hidden` so whatever still doesn't fit at its real
          // width is clipped rather than visually bleeding into the next
          // cell — a long-content demo is supposed to prove the CONTAINER
          // bounds the content, not spill past its own border. `fullWidth`
          // cells (the "responsive" state) span every column instead and
          // stay `overflow-x-auto` — they show genuinely wide content
          // (multiple breakpoint frames) that clipping would defeat the
          // purpose of demonstrating.
          className={`min-w-0 ${s.fullWidth ? 'overflow-x-auto sm:col-span-2 lg:col-span-3' : 'overflow-hidden'}`}
          data-testid={`state-matrix-${s.key}`}
        >
          <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{s.label}</dt>
          <dd className="mt-2 min-w-0">{s.children}</dd>
        </Surface>
      ))}
    </Grid>
  );
}
